<script lang="ts">
  import type { Incident } from '../lib/api/client.ts';
  import { BANNER_MS, ticker } from '../lib/ticker.svelte.ts';
  import { bannerJumpTarget, jumpToBanner } from '../lib/bannerJump.ts';
  import { isTv } from '../lib/platform.ts';
  import { playSound, type SoundKind } from '../lib/sounds.ts';

  /**
   * A goal in any followed match, announced across the upper part of the
   * picture: competition, both clubs with crests, the new score with the
   * scoring side lit, scorer and minute where the provider names them.
   * Red cards, second yellows and penalties use the same banner with their
   * own headline.
   *
   * Only transform and opacity are animated -- the compositor handles those
   * without layout work next to two decoding videos on a TV box.
   */

  const current = $derived(ticker.banners[0] ?? null);
  /** Set when one of the angles shows this match and is not the main picture. */
  const jumpTarget = $derived(current ? bannerJumpTarget() : null);
  /** The newest version of the incident -- its player may have been named since. */
  const incident = $derived.by(() => {
    const own = current?.incident;
    if (!own) return null;
    return current.fixture.extra?.incidents?.find((i) => i.id === own.id) ?? own;
  });
  const goal = $derived(incident ? null : current?.fixture.extra?.lastGoal ?? null);
  /** The provider's last goal belongs to this one only if its side -- and,
   *  where the provider says, the score it made -- match. */
  const detail = $derived(
    goal && current
      && (current.side === null || goal.side === current.side)
      && (!goal.score || (goal.score[0] === current.fixture.homeScore && goal.score[1] === current.fixture.awayScore))
      ? goal
      : null,
  );

  const SOUND: Record<Incident['kind'], SoundKind> = {
    red: 'whistleLong',
    yellowred: 'whistleLong',
    yellow: 'whistleShort',
    penaltyAwarded: 'whistleLong',
    penaltyMissed: 'groan',
  };

  /** Each banner sounds once, when it comes up -- not again while it waits in the queue. */
  let sounded = 0;
  $effect(() => {
    const c = current;
    if (!c || c.id === sounded) return;
    sounded = c.id;
    playSound(c.incident ? SOUND[c.incident.kind] : 'goal', ticker.style.sound);
  });

  const HEADLINE: Record<Incident['kind'], string> = {
    red: 'ROTE KARTE',
    yellowred: 'GELB-ROT',
    yellow: 'GELBE KARTE',
    penaltyAwarded: 'ELFMETER!',
    penaltyMissed: 'ELFMETER VERSCHOSSEN',
  };
  const ICON: Record<Incident['kind'], string> = {
    red: '🟥', yellowred: '🟨🟥', yellow: '🟨', penaltyAwarded: '❗', penaltyMissed: '❌',
  };

  /** No portrait is better than a broken-image icon. */
  function hide(e: Event): void {
    (e.currentTarget as HTMLImageElement).style.display = 'none';
  }

  function kind(g: NonNullable<typeof detail>): string {
    if (g.ownGoal) return 'Eigentor';
    if (g.penalty) return 'Elfmeter';
    return '';
  }
</script>

{#if current}
  {#key current.id}
    <!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_noninteractive_element_interactions -- OK on the remote jumps via App -->
    <div
      class="banner"
      class:card={incident && incident.kind !== 'penaltyAwarded' && incident.kind !== 'penaltyMissed'}
      class:jump={jumpTarget !== null}
      style:--life={`${BANNER_MS}ms`}
      role="status"
      aria-live="polite"
      onclick={() => jumpToBanner()}
    >
      <div class="head">
        <span class="tor">{incident ? HEADLINE[incident.kind] : 'TOR!'}</span>
        <span class="competition">{current.fixture.competition}</span>
      </div>
      <div class="match">
        <div class="team home" class:scored={current.side === 'home'}>
          {#if incident?.side === 'home'}<span class="badge">{ICON[incident.kind]}</span>{/if}
          {#if current.fixture.extra?.homeLogo}<img src={current.fixture.extra.homeLogo} alt="" />{/if}
          <span class="name">{current.fixture.homeTeam}</span>
        </div>
        <div class="score">
          <span class:lit={!incident && current.side === 'home'}>{current.fixture.homeScore ?? 0}</span>
          <span class="colon">:</span>
          <span class:lit={!incident && current.side === 'away'}>{current.fixture.awayScore ?? 0}</span>
        </div>
        <div class="team away" class:scored={current.side === 'away'}>
          <span class="name">{current.fixture.awayTeam}</span>
          {#if current.fixture.extra?.awayLogo}<img src={current.fixture.extra.awayLogo} alt="" />{/if}
          {#if incident?.side === 'away'}<span class="badge">{ICON[incident.kind]}</span>{/if}
        </div>
      </div>
      {#if incident && (incident.player || incident.minute)}
        <div class="scorer">
          {#if incident.photo}<img class="photo" src={incident.photo} alt="" onerror={hide} />{/if}
          <div class="who">
            {#if incident.player}<div class="player">{ICON[incident.kind]} {incident.player}</div>{/if}
            <div class="meta">
              {#if incident.minute}<span class="faint">{incident.minute}</span>{/if}
              {#if incident.kind === 'penaltyAwarded'}<span class="kind">nach VAR</span>{/if}
            </div>
          </div>
        </div>
      {:else if detail?.scorer || detail?.minute}
        <div class="scorer">
          {#if detail.photo && detail.scorer}<img class="photo" src={detail.photo} alt="" onerror={hide} />{/if}
          <div class="who">
            {#if detail.scorer}<div class="player">⚽ {detail.scorer}</div>{/if}
            <div class="meta">
              {#if detail.minute}<span class="faint">{detail.minute}</span>{/if}
              {#if kind(detail)}<span class="kind">{kind(detail)}</span>{/if}
            </div>
            {#if detail.assist}<div class="assist faint">Vorlage: {detail.assist}</div>{/if}
          </div>
        </div>
      {/if}
      {#if jumpTarget}
        <div class="go">▶ {isTv ? 'OK' : 'Klicken'}: zu diesem Spiel</div>
      {/if}
    </div>
  {/key}
{/if}

<style>
  .banner {
    position: absolute;
    left: 50%;
    top: 14%;
    z-index: 20;
    min-width: 46%;
    max-width: 80%;
    padding: 0.7em 1.4em 0.8em;
    border-radius: calc(var(--radius) * 1.5);
    background: linear-gradient(135deg, rgba(10, 16, 26, 0.94), rgba(18, 30, 46, 0.94));
    border: 2px solid var(--accent);
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.6);
    text-align: center;
    pointer-events: none;
    animation: life var(--life) ease-out both;
  }

  /* In with a small overshoot, a pause, out upwards. */
  @keyframes life {
    0% { transform: translate(-50%, -40px) scale(0.85); opacity: 0; }
    6% { transform: translate(-50%, 0) scale(1.04); opacity: 1; }
    10% { transform: translate(-50%, 0) scale(1); }
    90% { transform: translate(-50%, 0) scale(1); opacity: 1; }
    100% { transform: translate(-50%, -30px) scale(0.95); opacity: 0; }
  }

  .head { display: flex; align-items: baseline; justify-content: center; gap: 0.8em; }
  .tor {
    font-size: var(--font-xl);
    font-weight: 800;
    letter-spacing: 0.08em;
    color: var(--accent);
    animation: pulse 0.9s ease-in-out 3;
  }
  @keyframes pulse {
    50% { transform: scale(1.15); }
  }
  .competition { font-size: var(--font-sm); color: var(--text-dim); text-transform: uppercase; letter-spacing: 0.05em; }

  .match {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: 1em;
    margin-top: 0.4em;
    font-size: var(--font-lg);
  }
  .team { display: flex; align-items: center; gap: 0.5em; min-width: 0; }
  .team.home { justify-content: flex-end; }
  .team.away { justify-content: flex-start; }
  .team.scored .name { font-weight: 700; }
  .name { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  img { width: 1.6em; height: 1.6em; object-fit: contain; flex-shrink: 0; }

  .score {
    font-size: 1.4em;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    display: flex;
    gap: 0.2em;
  }
  .score .lit { color: var(--accent); }
  .colon { opacity: 0.6; }

  .scorer {
    margin-top: 0.5em;
    font-size: var(--font-base);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.9em;
  }
  /* Name on a line of its own: next to the minute it got cut short. */
  .who { text-align: left; min-width: 0; }
  .player { font-size: var(--font-lg); font-weight: 700; overflow-wrap: anywhere; }
  .meta:empty { display: none; }
  .meta .kind:first-child { margin-left: 0; }
  .scorer img.photo {
    flex-shrink: 0;
    width: 6.5em;
    height: 6.5em;
    border-radius: 50%;
    object-fit: cover;
    object-position: top;
    background: rgba(255, 255, 255, 0.1);
    border: 2px solid rgba(255, 255, 255, 0.25);
  }
  .badge { flex-shrink: 0; }
  .banner.card { border-color: #e5383b; }
  .banner.card .tor { color: #ff5a5f; }
  .assist { font-size: var(--font-sm); }

  .banner.jump { pointer-events: auto; cursor: pointer; }
  .go {
    margin-top: 0.5em;
    font-size: var(--font-sm);
    color: var(--accent);
    letter-spacing: 0.02em;
  }
  .kind {
    margin-left: 0.5em;
    padding: 0 0.4em;
    border-radius: 0.25em;
    background: rgba(255, 255, 255, 0.12);
    font-size: 0.85em;
  }
</style>
